import React, { useEffect, useState } from 'react';
import { ProjectsListSkeleton } from '../../../LoadersSkeletons/ProjectsListSkeleton/ProjectsListSkeleton';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  withStyles,
} from '@material-ui/core';
import { CriteriaType } from '../../../../models/PaginationIndicator';
import { ReactComponent as IconSort } from '../../../../assets/icons/u_sort.svg';
import { Link } from 'react-router-dom';
import { renderDateOrSlash } from '../../../../services/stringUtil';
import usePaginationSort from '../../../../hooks/usePaginationSort';
import { replaceMomentFormat } from '../../../../services/util';
import moment from 'moment';
import PaginationTable from '../../PaginationTable/PaginationTable';
import { Pagination } from '@material-ui/lab';
import styles from '../FinancialPlansSoonToExperieAndExpired.module.scss';
import { SelectFilterData } from './SelectFilterData';
import useDataNormalize from '../../../../hooks/useDataNormalize';
import { ExcelDownloadButton } from '../../../Util/ExcelDownloadButton/ExcelDownloadButton';
import { FilterTables } from '../../../Filter/FilterTables/FilterTables';
import { IndicatorKeyEnum } from '../../../../models/IndicatorKeyEnum';

type Props = {
  data: any;
  fnDataFilter: any;
  dataTabs: any;
  fnDataButtonsFilter: any;
  selectLabel: any;
  fnSelectLabel: any;
  isLoading: boolean;
};

const headers = [
  'code',
  'operation',
  'name',
  'new_plan_period',
  'new_plan_expiration_date',
  'new_last_advance_date',
  'new_minimum_amount_to_adjust',
  'new_total_amount_to_justify',
  'new_month_of_next_disbursement',
  'new_plan_status',
];

const selectedKeys = [
  'code',
  'loanNumber',
  'title',
  'field1',
  'field2',
  'field3',
  'field4',
  'field5',
  'field6',
  'field7',
];

export const FinancialPlansSoonToExperieAndExpiredTable = ({
  data,
  fnDataFilter,
  dataTabs,
  fnDataButtonsFilter,
  selectLabel,
  fnSelectLabel,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dataFilter, setDataFilter] = useState<any>(data);

  useEffect(() => {
    if (data) {
      setDataFilter(data);
    }
  }, [data]);

  const { handlePaginationChange, handleSort, currentList, pagesCount, currentPage } = usePaginationSort({
    projects: dataFilter,
    defaultSortCriteria: '',
    pageCount: rowsPerPage,
  });

  const sortTable = (keyToSort, fieldType) => {
    let criteriaType;
    switch (fieldType) {
      case 'numeric':
      case 'money':
      case 'percentage':
      case 'no_round_two_decimal':
        criteriaType = CriteriaType.number;
        break;
      case 'date':
        criteriaType = CriteriaType.date;
        break;
      default:
        criteriaType = CriteriaType.string;
        break;
    }
    handleSort(keyToSort, criteriaType);
  };

  const BlackTooltip = withStyles({
    tooltip: {
      backgroundColor: '#101319',
      fontSize: '16px',
      fontFamily: 'Rubik',
      lineHeight: '20px',
      padding: '8px 12px',
      borderRadius: '8px',
    },
    arrow: {
      fontSize: 20,
      color: '#4A4A4A',
      '&::before': {
        backgroundColor: '#101319',
      },
    },
  })(Tooltip);

  const dataExcel = useDataNormalize(headers, selectedKeys, dataFilter);

  return (
    <>
      <section>
        <div className={styles['container-filter']}>
          <div className={styles['filter-style']}>
            <SelectFilterData
              onChangeData={fnDataFilter}
              dataTabs={dataTabs}
              fnDataButtonsFilter={fnDataButtonsFilter}
              selectLabel={selectLabel}
              fnSelectLabel={fnSelectLabel}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <ExcelDownloadButton headers={headers} selectedKeys={selectedKeys} dataExcel={dataExcel} />
            <FilterTables
              projects={data}
              keysFilter={selectedKeys}
              fnDataFilter={setDataFilter}
              disabledNumber={currentPage}
              indicatorKey={IndicatorKeyEnum.FINANCIAL_PLANS_ABOUT_EXPIRE_EXPIRED}
            />
          </div>
        </div>

        <div className="indicator-detail-table-container">
          <TableContainer>
            {isLoading ? (
              <ProjectsListSkeleton />
            ) : (
              <div className="border-table">
                <Table>
                  <TableHead>
                    <TableRow style={{ maxHeight: '88px', minHeight: '88px', height: '88px' }}>
                      <TableCell onClick={() => sortTable('code', 'string')}>
                        <div className="text-head">
                          {t('code')} <IconSort className="sortIcon" />
                        </div>
                      </TableCell>
                      <TableCell onClick={() => sortTable('loanNumber', 'string')}>
                        <div className="text-head">
                          {t('operation')} <IconSort className="sortIcon" />
                        </div>
                      </TableCell>
                      <TableCell onClick={() => sortTable('title', 'string')}>
                        <div className="text-head">
                          {t('name')} <IconSort className="sortIcon" />
                        </div>
                      </TableCell>
                      <TableCell onClick={() => sortTable('field3', 'date')}>
                        <div className="text-head purchases_made">
                          {t('new_last_advance_date')} <IconSort className="sortIcon" />
                        </div>
                      </TableCell>
                      <TableCell onClick={() => sortTable('field1', 'numeric')}>
                        <div className="text-head">
                          {t('new_plan_period')} <IconSort className="sortIcon" />
                        </div>
                      </TableCell>
                      <TableCell onClick={() => sortTable('field2', 'date')}>
                        <div className="text-head">
                          {t('new_plan_expiration_date')} <IconSort className="sortIcon" />
                        </div>
                      </TableCell>

                      <TableCell onClick={() => sortTable('field4', 'numeric')}>
                        <div className="text-head">
                          {t('new_minimum_amount_to_adjust')} <IconSort className="sortIcon" />
                        </div>
                      </TableCell>
                      <TableCell onClick={() => sortTable('field5', 'numeric')}>
                        <div className="text-head">
                          {t('new_total_amount_to_justify')} <IconSort className="sortIcon" />
                        </div>
                      </TableCell>
                      <TableCell onClick={() => sortTable('field6', 'string')}>
                        <div className="text-head">
                          {t('new_month_of_next_disbursement')} <IconSort className="sortIcon" />
                        </div>
                      </TableCell>
                      <TableCell onClick={() => sortTable('field8', 'numeric')}>
                        <div className="text-head">
                          {t('new_plan_status')} <IconSort className="sortIcon" />
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataFilter?.length === 0
                      ? dataFilter?.map((row, i) => (
                          <TableRow key={i}>
                            <TableCell className="link-project">
                              <BlackTooltip title={t('go_to_project_info')} arrow>
                                <Link style={{ color: '#3D3E40' }} to={`/projectdetail?id=${row.id}`}>
                                  {row.code}
                                </Link>
                              </BlackTooltip>
                            </TableCell>
                            <TableCell>{row.loanNumber}</TableCell>
                            <TableCell className="name-project">
                              <BlackTooltip
                                title={row.title}
                                placement="bottom"
                                enterDelay={500}
                                leaveDelay={100}
                                arrow
                              >
                                <p className=" name-project-nowrap"> {row.title}</p>
                              </BlackTooltip>
                            </TableCell>
                            <TableCell>{row.field1}</TableCell>
                            <TableCell>{renderDateOrSlash(row.field2)}</TableCell>
                            <TableCell>{row.field3}</TableCell>
                            <TableCell>{Math.trunc(row.field4)} %</TableCell>
                          </TableRow>
                        ))
                      : currentList?.map((row, i) => (
                          <TableRow key={i}>
                            <TableCell className="link-project">
                              <BlackTooltip title={t('go_to_project_info')} arrow>
                                <Link style={{ color: '#3D3E40' }} to={`/projectdetail?id=${row.id}`}>
                                  {row.code}
                                </Link>
                              </BlackTooltip>
                            </TableCell>
                            <TableCell>{row.loanNumber}</TableCell>
                            <TableCell className="name-project">
                              <BlackTooltip
                                title={row.title}
                                placement="bottom"
                                enterDelay={500}
                                leaveDelay={100}
                                arrow
                              >
                                <p className=" name-project-nowrap"> {row.title}</p>
                              </BlackTooltip>
                            </TableCell>
                            <TableCell>
                              {row.field3 ? replaceMomentFormat(moment(row.field3).format('DD/MMM/YYYY')) : '-'}
                            </TableCell>
                            <TableCell>{row.field1 !== null ? `${row.field1}${t('days')}` : '-'}</TableCell>
                            <TableCell>
                              <TableCell>
                                {row.field2 ? replaceMomentFormat(moment(row.field2).format('DD/MMM/YYYY')) : '-'}
                              </TableCell>
                            </TableCell>
                            <TableCell>
                              <NumberFormat
                                value={row.field4}
                                displayType={'text'}
                                thousandSeparator={','}
                                decimalSeparator={'.'}
                                decimalScale={1}
                              />
                            </TableCell>
                            <TableCell>
                              <NumberFormat
                                value={row.field5}
                                displayType={'text'}
                                thousandSeparator={','}
                                decimalSeparator={'.'}
                                decimalScale={1}
                              />
                            </TableCell>
                            <TableCell>{row.field6 ? t(row.field6) : '-'}</TableCell>
                            <TableCell>
                              {row.field7 === 'RED' && row.field8 !== null ? (
                                <p className={styles['style-color-red']}>
                                  {`${t('efas_pending_states_3')}`} <span>{`${row.field8} ${t('days')}`}</span>
                                </p>
                              ) : row.field7 === 'YELLOW' && row.field8 !== null ? (
                                <p className={styles['style-color-yellow']}>
                                  {`${t('new_to_win_in')}`} <span>{`${row.field8} ${t('days')}`}</span>
                                </p>
                              ) : row.field7 === 'GREEN' && row.field8 !== null ? (
                                <p className={styles['style-color-green']}>
                                  {`${t('new_within_the_deadline')}`} <span>{`${row.field8} ${t('days')}`}</span>
                                </p>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TableContainer>

          <div className="pagination-style">
            <PaginationTable fnSetRows={setRowsPerPage} rows={rowsPerPage} numProyects={dataFilter?.length} />
            {dataFilter?.length !== 0 ? (
              <Pagination count={pagesCount} page={currentPage} onChange={handlePaginationChange} />
            ) : null}
          </div>
        </div>
      </section>
    </>
  );
};
